import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckSquare, Square, PackageCheck, Search } from 'lucide-react';
import { PageHeader, DataTable } from '../../components/common';
import { getUserWarehouses, searchEligibleOrders, createPackingBatch } from '../../services/api/packingBatch';
import { getBusinessCouriers } from '../../services/api/businessCourier';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import styles from './OfflineSalesPackingPage.module.css';

const OfflineSalesPackingPage = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const [warehouses, setWarehouses] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [businessCourierId, setBusinessCourierId] = useState('');
  const [search, setSearch] = useState('');
  const [orders, setOrders] = useState([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 20, total: 0 });
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    Promise.all([getUserWarehouses(), getBusinessCouriers()])
      .then(([warehouseResult, courierResult]) => {
        setWarehouses(warehouseResult.warehouses || []);
        setCouriers(courierResult.couriers || []);
      })
      .catch(error => showError(error.response?.data?.message || t('offlineSales.failedToLoadPackingReferences')));
  }, [showError, t]);

  const loadOrders = async (page = pagination.currentPage) => {
    if (!warehouseId || !businessCourierId) {
      showError(t('offlineSales.selectPackingReferences'));
      return;
    }
    try {
      setLoading(true);
      const result = await searchEligibleOrders({
        warehouseId,
        businessCourierId,
        channel: 'offline',
        orderStatusKeys: 'confirmed,in_packing',
        search,
        page,
        limit: pagination.pageSize,
      });
      setOrders(result.orders || []);
      setSelectedOrderIds([]);
      setPagination(prev => ({ ...prev, currentPage: result.page || page, total: result.total || 0 }));
    } catch (error) {
      showError(error.response?.data?.message || t('offlineSales.failedToLoadPackingOrders'));
    } finally {
      setLoading(false);
    }
  };

  const toggleOrder = orderId => {
    setSelectedOrderIds(prev => prev.includes(orderId)
      ? prev.filter(id => id !== orderId)
      : [...prev, orderId]);
  };

  const handleCreateBatch = async () => {
    if (!selectedOrderIds.length) {
      showError(t('offlineSales.selectPackingOrders'));
      return;
    }
    try {
      setCreating(true);
      const result = await createPackingBatch({ businessCourierId, warehouseId, orderIds: selectedOrderIds, channel: 'offline' });
      success(t('offlineSales.packingBatchCreated'));
      navigate(`/packing-batches/${result.batchId}`);
    } catch (error) {
      showError(error.response?.data?.message || t('offlineSales.failedToCreatePackingBatch'));
    } finally {
      setCreating(false);
    }
  };

  const columns = [
    {
      key: 'select',
      header: '',
      width: '48px',
      render: row => (
        <button
          type="button"
          onClick={() => toggleOrder(row.id)}
          className={styles.checkboxButton}
          aria-label={t('offlineSales.selectPackingOrder', { orderNumber: row.orderNumber })}
        >
          {selectedOrderIds.includes(row.id) ? <CheckSquare size={18} /> : <Square size={18} />}
        </button>
      ),
    },
    { key: 'orderNumber', header: t('offlineSales.orderNumber'), render: row => row.orderNumber },
    { key: 'customerName', header: t('offlineSales.customer'), render: row => row.customerName || t('common.na') },
    { key: 'locationDisplay', header: t('offlineSales.deliveryCity'), render: row => row.locationDisplay || t('offlineSales.notSet') },
    { key: 'grandTotal', header: t('offlineSales.total'), render: row => row.grandTotal || t('common.na') },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('offlineSales.offlinePacking')}
        description={t('offlineSales.offlinePackingDescription')}
        icon={PackageCheck}
      />

      <section className={styles.controls}>
        <div className={styles.field}>
          <label htmlFor="offline-packing-warehouse">{t('offlineSales.warehouse')}</label>
          <Dropdown
            value={warehouseId}
            onChange={value => setWarehouseId(value || '')}
            options={warehouses.map(warehouse => ({ value: warehouse.id, label: warehouse.name }))}
            placeholder={t('offlineSales.selectWarehouse')}
            searchable
          />
        </div>
        <div className={styles.field}>
          <label htmlFor="offline-packing-courier">{t('offlineSales.courierAccount')}</label>
          <Dropdown
            value={businessCourierId}
            onChange={value => setBusinessCourierId(value || '')}
            options={couriers.map(courier => ({ value: courier.id, label: courier.accountName }))}
            placeholder={t('offlineSales.selectCourierAccount')}
            searchable
          />
        </div>
        <div className={styles.searchField}>
          <label htmlFor="offline-packing-search">{t('common.search')}</label>
          <div className={styles.searchInput}><Search size={16} /><input id="offline-packing-search" value={search} onChange={event => setSearch(event.target.value)} placeholder={t('offlineSales.searchPackingOrders')} /></div>
        </div>
        <button type="button" className={styles.primaryButton} onClick={() => loadOrders(1)} disabled={loading}><Search size={16} /> {t('common.search')}</button>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableToolbar}>
          <span>{t('offlineSales.selectedPackingOrders', { count: selectedOrderIds.length })}</span>
          <button type="button" className={styles.primaryButton} onClick={handleCreateBatch} disabled={creating || !selectedOrderIds.length}><CheckSquare size={16} /> {t('offlineSales.createPackingBatch')}</button>
        </div>
        <DataTable
          data={orders}
          columns={columns}
          loading={loading}
          pagination={{ ...pagination, pageSizeOptions: [20, 50, 100] }}
          onPaginationChange={next => { setPagination(next); loadOrders(next.currentPage); }}
          emptyMessage={t('offlineSales.noPackingOrders')}
          emptyDescription={t('offlineSales.noPackingOrdersDescription')}
          emptyIcon={<PackageCheck size={56} />}
        />
      </section>
    </div>
  );
};

export default OfflineSalesPackingPage;